import { Button, Group, Box, useMantineTheme, Flex, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import {
  CreateEmployeeRoleInput,
  RolePermissionType,
  useCreateEmployeeRoleMutation,
  useGetEmployeeRoleQuery,
  useUpdateEmployeeRoleMutation,
} from "src/generated/graphql";
import { memo, useRef, useCallback } from "react";
import { object, string } from "yup";
import Select from "src/components/Select";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import { closeAllModals } from "@mantine/modals";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

type RoleFormProps = {
  setrefetchTableData: (bool: boolean) => void;
  empRoleId?: string;
  refetchTableData: boolean;
  isAllowWrite: boolean;
  seedId?: string;
};
type IFormProps = CreateEmployeeRoleInput;

const EmployeeRoleFormSchema = object().shape({
  name: string().trim("No spaces allowed").strict(true).required().max(30),
  description: string().max(200).notRequired(),
});

const EmployeeRolesObj = Object.keys(EMPLOYEE_ROLE_OBJ).reduce((acc, key) => {
  let fullName = key
    .replace(/loc([A-Z])/g, (_, match) => `Location ${match}`)
    .replace(/org([A-Z])/g, (_, match) => `Organization ${match}`)
    .replace(/clients/, () => `Accounts`)
    .replace(/([a-z])([A-Z])/g, "$1 $2"); // Insert space between camelCase
  // Uppercase the first character of each word
  fullName = fullName.replace(/\b\w/g, (char) => char.toUpperCase());
  acc[key] = fullName;
  return acc;
}, {} as any);

const createInitObj = (): Record<string, RolePermissionType> => {
  return Object.keys(EmployeeRolesObj).reduce((acc, cur) => {
    acc[cur] = RolePermissionType.Read;
    return acc;
  }, {} as Record<string, RolePermissionType>);
};

const initialValues = {
  name: "",
  description: "",
  ...createInitObj(),
};
const RoleForm = ({
  setrefetchTableData,
  empRoleId,
  refetchTableData,
  isAllowWrite,
  seedId = "",
}: RoleFormProps) => {
  const theme = useMantineTheme();
  const isRefetch = useRef(refetchTableData);

  const form = useForm<IFormProps>({
    initialValues,
    validate: yupResolver(EmployeeRoleFormSchema),
  });

  useGetEmployeeRoleQuery({
    variables: { id: empRoleId! },
    skip: !empRoleId,
    onCompleted(data) {
      if (!data.employeeRoleOne) return;
      const { id, ...rest } = data.employeeRoleOne;
      form.setValues(rest);
      form.resetDirty(rest);
    },
    fetchPolicy: "network-only",
  });

  const [createEmployeeRole, { loading }] = useCreateEmployeeRoleMutation({
    onCompleted: () => {
      setrefetchTableData(true);
      form.resetDirty();
      closeAllModals();
    },
  });

  const [updateEmployeeRole, { loading: updateLoading }] =
    useUpdateEmployeeRoleMutation({
      onCompleted: () => {
        isRefetch.current = !isRefetch.current;
        setrefetchTableData(isRefetch.current);
        form.resetDirty();
        closeAllModals();
      },
    });

  const handleSubmit = async (values: IFormProps) => {
    const obj = values;
    empRoleId
      ? await updateEmployeeRole({
          variables: { data: obj, id: empRoleId },
        })
      : await createEmployeeRole({
          variables: { data: obj },
        });
  };

  const RoleFields = useCallback(() => {
    return Object.entries(EmployeeRolesObj).map(([k, value]) => {
      return (
        <Flex justify={"space-between"} align={"center"} key={k}>
          <Text weight={500} sx={{ width: "70%" }}>
            {value}
          </Text>
          <Select
            data={Object.keys(RolePermissionType)}
            label={`Permission`}
            {...form.getInputProps(k)}
            disabled={
              !isAllowWrite || seedId?.trim()?.length > 0 ? true : false
            }
          />
        </Flex>
      );
    });
  }, [form, isAllowWrite]);

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <form
        onSubmit={form.onSubmit((values) => {
          handleSubmit(values);
        })}
      >
        <TextInput
          withAsterisk
          label="Name"
          {...form.getInputProps("name")}
          disabled={!isAllowWrite || seedId?.trim()?.length > 0 ? true : false}
        />

        <Textarea
          label="Description"
          withAsterisk
          autosize
          {...form.getInputProps("description")}
          disabled={!isAllowWrite || seedId?.trim()?.length > 0 ? true : false}
        />

        {RoleFields()}

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={() => closeAllModals()}
            disabled={loading}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          {isAllowWrite && (
            <Button
              radius="md"
              type="submit"
              disabled={
                loading || updateLoading || seedId?.trim()?.length > 0
                  ? true
                  : false
              }
              style={{
                flex: 1,
              }}
              sx={{
                color: theme.colors.primaryTextColor[6],
                "&:hover": {
                  backgroundColor: theme.colors.primary[4],
                },
              }}
              loading={loading || updateLoading}
            >
              {empRoleId ? "Update" : " Create"}
            </Button>
          )}
        </Group>
      </form>
    </Box>
  );
};

export default memo(RoleForm);
